import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { stripHistoryLink, wantsHistoryOpen, wantsTransactionsPanel } from '@/lib/migratedHistoryLink';

interface Options {
  /** The debts are still arriving; nothing can be decided yet. */
  isLoading: boolean;
  /** False when the debt has no transactions and no migrated history to show. */
  hasTransactionsTab: boolean;
  /** Called once, when the link is honoured. */
  onOpenTransactions: () => void;
}

/**
 * Consumes the "see your earlier payments" link from the details page, and reports whether
 * the history card should arrive already open.
 *
 * Two things make this more than a query-string read:
 *
 * The link is an in-app navigation, so the query changes without the page remounting and a
 * `useState` initialiser never re-runs. It has to be an effect.
 *
 * And the link is stripped once consumed - left in place the URL stops changing, so a second
 * click is inert, the customer cannot keep the card closed, and a shared link re-applies a
 * stale panel. Stripping is deferred until the debts have loaded: `hasTransactionsTab` is
 * false on the first run of a cold load, and stripping then would discard the request before
 * it could be honoured, which is every arrival that is not an in-app click.
 */
export function useMigratedHistoryDeepLink({ isLoading, hasTransactionsTab, onOpenTransactions }: Options): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openOnArrival, setOpenOnArrival] = useState(() => wantsHistoryOpen(`?${searchParams}`));

  useEffect(() => {
    if (!wantsTransactionsPanel(`?${searchParams}`)) return;
    if (isLoading) return;
    if (hasTransactionsTab) {
      onOpenTransactions();
      setOpenOnArrival(wantsHistoryOpen(`?${searchParams}`));
    }
    setSearchParams(stripHistoryLink(searchParams), { replace: true });
  }, [searchParams, setSearchParams, hasTransactionsTab, isLoading, onOpenTransactions]);

  return openOnArrival;
}
